import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AnalyticsApiService from '../../ApiServices/AnalyticsApiService/AnalyticsApiService';
import { useAuth } from '../Auth/AuthContext';
import AddAnalyticsForm from './AddAnalyticsForm';
import EditForm from './EditForm';
import Datepicker from "react-tailwindcss-datepicker";
import DataTable from 'react-data-table-component';
import { motion } from "framer-motion";
import {
  FaDollarSign,
  FaMoneyBillWave,
  FaMousePointer,
  FaChartBar,
  FaServer,
  FaPercentage,
  FaChartPie,
  FaCompressArrowsAlt,
  FaChartLine,
} from "react-icons/fa";

const AppAnalyticsTable = () => {
  const [analytics, setAnalytics] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState("");
  const [selectedApp, setSelectedApp] = useState("");
  const [selectedAdunit, setSelectedAdunit] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });

  const Auth = useAuth();
  const user = Auth.authState.user;
  const role = Auth.authState.roles;
  const admin = role === "Admin";

  // Modify filter change functions
  const handleFilterChange = (setter, value) => {

    setFilterLoading(true);
    setTimeout(() => {
      setter(value);
      setFilterLoading(false);
    }, 800);
  };
  const fetchAnalytics = async () => {
    try {
      setFilterLoading(true);
      const response = admin
        ? await AnalyticsApiService.getAllAppAnalytics()
        : await AnalyticsApiService.GetUserAppAnalytics(user.id);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics.');
    } finally {
      setFilterLoading(false);
    }
  };



  useEffect(() => {
    if (user && user.id) {
      fetchAnalytics();
    }
  }, [user]);

  const uniquePublishers = [...new Set(analytics.map(entry => entry.publisher))];
  const uniqueApps = [...new Set(analytics.map(entry => entry.app))];
  const uniqueAdunits = [...new Set(analytics.map(entry => entry.adUnit))];

  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const filteredAnalytics = analytics.filter(entry => {
    const entryDate = normalizeDate(entry.date);
    const start = dateRange.startDate ? normalizeDate(dateRange.startDate) : null;
    const end = dateRange.endDate ? normalizeDate(dateRange.endDate) : null;

    const isWithinRange =
      (!start || entryDate >= start) &&
      (!end || entryDate <= end);

    return (
      (selectedPublisher ? entry.publisher === selectedPublisher : true) &&
      (selectedApp ? entry.app === selectedApp : true) &&
      (selectedAdunit ? entry.adUnit === selectedAdunit : true) &&
      isWithinRange
    );
  });

  const totalStats = filteredAnalytics.reduce(
    (acc, entry) => {
      acc.totalRevenue += parseFloat(entry.adExchangeRevenue || 0);
      acc.totalPayout += parseFloat(entry.adExchangePayout || 0);
      acc.totalClicks += Number(entry.adExchangeClicks || 0);
      acc.totalImpressions += Number(entry.adExchangeImpressions || 0);
      acc.totalRequests += Number(entry.adExchangeAdRequests || 0);
      acc.totalCTR += parseFloat(entry.adExchangeCTR || 0);
      acc.totalECPM += parseFloat(entry.adExchangeAvgECPM || 0);
      acc.totalMatchRate += parseFloat(entry.adExchangeMatchRate || 0);
      acc.totalFillRate += parseFloat(entry.adExchangeFillRate || 0);
      acc.count += 1;
      return acc;
    },
    {
      totalRevenue: 0,
      totalPayout: 0,
      totalClicks: 0,
      totalImpressions: 0,
      totalRequests: 0,
      totalCTR: 0,
      totalECPM: 0,
      totalMatchRate: 0,
      totalFillRate: 0,
      count: 0,
    }
  );

  // Averages
  const avgCTR = totalStats.count ? (totalStats.totalCTR / totalStats.count).toFixed(2) : 0;
  const avgECPM = totalStats.count ? (totalStats.totalECPM / totalStats.count).toFixed(2) : 0;
  const avgMatchRate = totalStats.count ? (totalStats.totalMatchRate / totalStats.count).toFixed(2) : 0;
  const avgFillRate = totalStats.count ? (totalStats.totalFillRate / totalStats.count).toFixed(2) : 0;

  const summaryCards = [
    {
      title: "Total Revenue",
      value: `$${totalStats.totalRevenue.toFixed(2)}`,
      icon: <FaDollarSign />,
      colors: "from-indigo-500 to-purple-500",
    },
    {
      title: "Total Payout",
      value: `$${totalStats.totalPayout.toFixed(2)}`,
      icon: <FaMoneyBillWave />,
      colors: "from-pink-500 to-red-500",
    },
    {
      title: "Total Clicks",
      value: totalStats.totalClicks,
      icon: <FaMousePointer />,
      colors: "from-green-500 to-emerald-400",
    },
    {
      title: "Total Impressions",
      value: totalStats.totalImpressions,
      icon: <FaChartBar />,
      colors: "from-yellow-500 to-orange-400",
    },
    {
      title: "Total Requests",
      value: totalStats.totalRequests,
      icon: <FaServer />,
      colors: "from-blue-500 to-cyan-400",
    },
    {
      title: "Avg CTR",
      value: `${avgCTR}%`,
      icon: <FaPercentage />,
      colors: "from-purple-500 to-indigo-400",
    },
    {
      title: "Avg eCPM",
      value: `$${avgECPM}`,
      icon: <FaChartPie />,
      colors: "from-fuchsia-500 to-pink-400",
    },
    {
      title: "Avg Match Rate",
      value: `${avgMatchRate}%`,
      icon: <FaCompressArrowsAlt />,
      colors: "from-teal-500 to-green-400",
    },
    {
      title: "Avg Fill Rate",
      value: `${avgFillRate}%`,
      icon: <FaChartLine />,
      colors: "from-rose-500 to-red-400",
    },
  ];

  const [filterLoading, setFilterLoading] = useState(false);

  const handleResetFilters = () => {
    setFilterLoading(true);
    setTimeout(() => {
      setSelectedPublisher("");
      setSelectedApp("");
      setDateRange({ startDate: null, endDate: null });
      setFilterLoading(false);
    }, 800); // Simulate a delay
  };

  // Refresh analytics data when a new entry is submitted
  const handleAnalyticsSubmit = () => {
    fetchAnalytics(); // Call the function to refresh data
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      try {
        await AnalyticsApiService.deleteAppAnalytics(id);
        setAnalytics(analytics.filter(entry => entry.id !== id));
        toast.success('Delete successfully!');
      } catch (error) {
        console.error("Error deleting analytics:", error);
      }
    }
  };

  // Handle Edit
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setIsEditing(true);
  };

  // Handle Update
  const handleUpdate = async (updatedEntry) => {
    try {
      const obj = {
        id: updatedEntry.id,
        adExchangeImpressions: Number(updatedEntry.adExchangeImpressions),
        adExchangeClicks: Number(updatedEntry.adExchangeClicks),
        adExchangeCTR: parseFloat(updatedEntry.adExchangeCTR),
        adExchangeRevenue: parseFloat(updatedEntry.adExchangeRevenue),
        adExchangePayout: parseFloat(updatedEntry.adExchangePayout),
        adExchangeAvgECPM: parseFloat(updatedEntry.adExchangeAvgECPM),
        adExchangeAdRequests: Number(updatedEntry.adExchangeAdRequests),
        adExchangeMatchRate: parseFloat(updatedEntry.adExchangeMatchRate),
        adExchangeFillRate: parseFloat(updatedEntry.adExchangeFillRate),
      };

      var response = await AnalyticsApiService.updateAppAnalytics(updatedEntry.id, obj);
      toast.success(response.message);
      setAnalytics(analytics.map(entry => (entry.id === updatedEntry.id ? updatedEntry : entry)));
      setEditingEntry(null);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating analytics:", error);
    }
  };

  const columns = [
    {
      name: 'Date',
      selector: row => new Date(row.date).toLocaleDateString('en-CA'), // ✅ Correct local date
      sortable: true,
      width: '150px',
    },
    // Conditionally include "Publisher" column for admin
    ...(admin ? [
      {
        name: 'Publisher',
        selector: row => row.publisher,
        sortable: true,
        width: '160px',
      }
    ] : []),
    {
      name: 'App',
      selector: row => row.app,
      sortable: true,
      width: '160px',
    },
    {
      name: 'Adunits',
      selector: row => row.adUnit,
      sortable: true,
      width: '140px',
    },
    {
      name: 'Impressions',
      selector: row => row.adExchangeImpressions,
      sortable: true,
      width: '160px',
    },
    {
      name: 'Clicks',
      selector: row => row.adExchangeClicks,
      sortable: true,
      width: '120px',
    },
    {
      name: 'CTR (%)',
      selector: row => `${row.adExchangeCTR}%`,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Revenue ($)',
      selector: row => `$${row.adExchangeRevenue}`,
      sortable: true,
      width: '160px',
    },
    {
      name: 'Payout',
      selector: row => row.adExchangePayout,
      sortable: true,
      width: '120px',
    },
    {
      name: 'eCPM ($)',
      selector: row => `$${row.adExchangeAvgECPM}`,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Requests',
      selector: row => row.adExchangeAdRequests,
      sortable: true,
      width: '140px',
    },
    {
      name: 'Match Rate (%)',
      selector: row => `${row.adExchangeMatchRate}%`,
      sortable: true,
      width: '160px',
    },
    {
      name: 'Fill Rate (%)',
      selector: row => `${row.adExchangeFillRate}%`,
      sortable: true,
      width: '160px',
    },
    ...(admin ? [
      {
        name: 'Actions',
        cell: row =>
          admin && (
            <div className="flex gap-2">
              <button
                className="px-3 py-1 text-xs font-semibold text-white bg-blue-500 rounded hover:bg-blue-600 transition"
                onClick={() => handleEdit(row)}
              >
                Edit
              </button>
              <button
                className="px-3 py-1 text-xs font-semibold text-white bg-red-500 rounded hover:bg-red-600 transition"
                onClick={() => handleDelete(row.id)}
              >
                Delete
              </button>
            </div>
          ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '220px',
      }
    ] : []),
  ];

  return (
    <div className="p-6 bg-gradient-to-br from-gray-100 to-gray-300 min-h-screen">
      <ToastContainer />

      {/* Filters Section */}
      <div className="relative bg-white/80 backdrop-blur-sm shadow-2xl rounded-2xl p-6 mb-8 border border-gray-200 overflow-visible z-40">

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {/* Publisher Filter */}
          {admin && (
            <div>
              <label className="block text-gray-800 text-sm font-bold mb-2">Publisher</label>
              <select
                className="w-full bg-white border border-gray-300 px-4 py-2 rounded-xl shadow-md focus:ring-2 focus:ring-indigo-500 transition-all"
                value={selectedPublisher}
                onChange={(e) => handleFilterChange(setSelectedPublisher, e.target.value)}
              >
                <option value="">All Publishers</option>
                {uniquePublishers.map((publisher) => (
                  <option key={publisher} value={publisher}>{publisher}</option>
                ))}
              </select>
            </div>
          )}

          {/* App Filter */}
          <div>
            <label className="block text-gray-800 text-sm font-bold mb-2">Apps</label>
            <select
              className="w-full bg-white border border-gray-300 px-4 py-2 rounded-xl shadow-md focus:ring-2 focus:ring-indigo-500 transition-all"
              value={selectedApp}
              onChange={(e) => handleFilterChange(setSelectedApp, e.target.value)}
            >
              <option value="">All Apps</option>
              {uniqueApps.map((app) => (
                <option key={app} value={app}>{app}</option>
              ))}
            </select>
          </div>

          {/* Adunit Filter */}
          <div>
            <label className="block text-gray-800 text-sm font-bold mb-2">Adunit</label>
            <select
              className="w-full bg-white border border-gray-300 px-4 py-2 rounded-xl shadow-md focus:ring-2 focus:ring-indigo-500 transition-all"
              value={selectedAdunit}
              onChange={(e) => handleFilterChange(setSelectedAdunit, e.target.value)}
            >
              <option value="">All Adunits</option>
              {uniqueAdunits.map((adunit) => (
                <option key={adunit} value={adunit}>{adunit}</option>
              ))}
            </select>
          </div>

          {/* Date Range Picker */}
          <div className="relative z-40">
            <label className="block text-gray-800 text-sm font-bold mb-2">Date Range</label>
            <Datepicker
              value={dateRange}
              onChange={(value) => handleFilterChange(setDateRange, value)}
              placeholder="Select Date Range"
              showShortcuts={true}
              popperPlacement="bottom-end"
              containerClassName="relative z-50"
              inputClassName="w-full border border-gray-300 px-4 py-2 rounded-xl shadow-md focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Reset Button */}
          <div className="flex items-end">
            <button
              onClick={handleResetFilters}
              className="w-full bg-gray-600 text-white font-medium px-4 py-2 rounded-xl shadow-md hover:bg-gray-700 transition-all"
            >
              Reset Filters
            </button>
          </div>

          {/* Add Button */}
          {admin && (
            <div className="flex items-end">
              <button
                onClick={() => setModalIsOpen(true)}
                className="w-full bg-indigo-500 text-white font-semibold px-5 py-2 rounded-xl shadow-md hover:bg-indigo-600 transition-all"
              >
                + Add Analytics
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white/90 backdrop-blur-sm shadow-2xl rounded-2xl p-6">
        {filterLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white/70 backdrop-blur-md p-8 rounded-2xl shadow-xl flex flex-col items-center"
          >
            <div className="w-14 h-14 border-4 border-dashed border-indigo-500 rounded-full animate-spin"></div>
            <p className="mt-4 text-indigo-800 text-base font-semibold tracking-wide animate-pulse">
              Loading analytics data...
            </p>
          </motion.div>
        ) : (
          <DataTable
            title="📈 App Analytics Report"
            columns={columns}
            data={filteredAnalytics}
            pagination
            highlightOnHover
            responsive
            dense
            defaultSortFieldId={1}
            defaultSortAsc={false}
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </div>

      {/* Summary Section */}
      {!filterLoading && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-12 bg-white shadow-2xl rounded-2xl p-8"
        >
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              📊 Analytics Summary
            </h3>
            <p className="text-sm text-gray-500 mt-1">Quick insights from filtered data</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
            {summaryCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className={`bg-gradient-to-br ${card.colors} text-white p-6 rounded-2xl shadow-xl hover:scale-[1.03] transform transition-all duration-300`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-3xl">{card.icon}</div>
                  <div>
                    <p className="text-sm">{card.title}</p>
                    <p className="text-2xl font-bold mt-1">{card.value}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Modals */}
      {isEditing && (
        <EditForm
          entry={editingEntry}
          onUpdate={handleUpdate}
          onCancel={() => setIsEditing(false)}
        />
      )}
      {modalIsOpen && (
        <AddAnalyticsForm
          isOpen={modalIsOpen}
          onRequestClose={() => setModalIsOpen(false)}
          onSubmit={handleAnalyticsSubmit}
        />
      )}
    </div>
  );

};

export default AppAnalyticsTable;
